import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Name',
    width: '20%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },
  {
    name: 'businessMetadata.asset_id',
    label: 'Asset ID',
    width: '10%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_id'],
    },
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    width: '8%',
    filter: {
      type: 'aggregation',
      aggregationName: 'env_aggregation',
      remoteSearchFields: ['env'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    remoteSortField: 'env',
  },
  {
    name: 'businessMetadata.owner',
    label: 'Owner',
    width: '10%',
    remoteSortField: 'owner',
    filter: {
      type: 'text',
      remoteSearchFields: ['owner'],
    },
  },
  {
    name: 'businessMetadata.administrativeMetadata.team',
    label: 'Team',
    width: '10%',
    remoteSortField: 'team',
    filter: {
      type: 'text',
      remoteSearchFields: ['team'],
    },
  },
  {
    name: 'sourceSpecificMetadata.LookerDashboard.dash_total_score',
    label: 'Dash Total Score',
    width: '10%',
    remoteSortField: 'dash_total_score',
    sortable: true,
  },
  {
    name: 'sourceSpecificMetadata.LookerDashboard.level_of_impact',
    label: 'Level of Impact',
    width: '10%',
    remoteSortField: 'level_of_impact',
    filter: {
      type: 'aggregation',
      aggregationName: 'level_of_impact_aggregation',
      remoteSearchFields: ['level_of_impact'],
      filterSelectProps: {
        isClearable: false,
      },
    },
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    width: '10%',
    remoteSortField: 'description',
  },
  { name: 'links', label: 'External URL', width: '10%' },
  { name: 'lineage', label: 'Lineage', width: '10%' },
];

const attributesMap: Attribute[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Name',
    showInColumn: 0,
    description: 'Name of the dashboard',
  },
  {
    name: 'businessMetadata.asset_id',
    label: 'Id',
    showInColumn: 0,
    description: 'Id of the dashboard',
  },
  {
    name: 'businessMetadata.owner',
    label: 'Owner',
    showInColumn: 2,
    description: 'Owner',
    isSearchable: true,
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    showInColumn: 0,
    description: 'Asset environment',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Asset Type',
    showInColumn: 0,
    description: 'Asset type',
  },
  {
    name: 'technicalMetadata.asset_status',
    label: 'Asset Status',
    showInColumn: 0,
    description: 'Asset status',
  },
  {
    name: 'sourceSpecificMetadata.LookerDashboard.external_url',
    label: 'External Url',
    showInColumn: 1,
    description: 'External Url',
    type: 'link',
  },
  {
    name: 'businessMetadata.administrativeMetadata.team',
    label: 'Team',
    showInColumn: 0,
    description: 'Team',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.LookerDashboard.last_ingested',
    label: 'Last Ingested',
    showInColumn: 1,
    description: 'Last Ingested',
    type: 'date',
  },
  {
    name: 'sourceSpecificMetadata.LookerDashboard.level_of_impact',
    label: 'Level of Impact',
    showInColumn: 1,
    description: 'Level of impact',
    type: 'string',
  },
  {
    name: 'sourceSpecificMetadata.LookerDashboard.dash_total_score',
    label: 'Dash Total Score',
    showInColumn: 1,
    description: 'Dash total score',
    type: 'string',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    showInColumn: 2,
    description: 'Description',
  },
];

const customQueryForListPage = `
  LookerDashboard {
    external_url
    last_ingested
    dash_total_score
    level_of_impact
  }
`;

const customQueryForDetailsPage = `
  LookerDashboard {
    external_url
    last_ingested
    dash_total_score
    level_of_impact
  }
`;

const searchableFields = getSearchableFields(attributesMap);

export const LookerDashboardConfig: DataSource = {
  id: '8',
  name: 'LookerDashboard',
  showColumns: false,
  showPartitionColumns: false,
  showLineage: true,
  showLineageTable: true,
  showLineagePipeline: false,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineageColumn: false,
  showLineageDashboard: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  glossary: false,
  searchableFields,
};
